import React, { useState, useEffect } from "react";
import "./editSocialLinkPopup.scss";
import Modal from "react-bootstrap/Modal";
import {
  instaIcon,
  linkedInIcon,
  modalClose,
  webIcon,
  webIconOrange,
  lockIcon,
  plusicon,
} from "../../../../../images";
import Joi from "joi";
import http from "../../../../../helpers/http";
import config from "../../../../../config/config";
import { toast } from "react-toastify";
import ToastMsg from "../../../../../components/ToastMsg/ToastMsg";
import { useBusinessContext } from "../../../../../context/BusinessAccount/BusinessAccountState";
import targetAttributesFile from "../../../../../config/target_attributes.json";
import useTheme from "../../../../../components/useTheme/useTheme";

const EditSocialLinkPopup = (props) => {
  const { theme } = useTheme();
  const [formData, setFormData] = useState({
    website_link: "",
    instagram_handle: "",
    linkedin_link: "",
  });
  const [hasChanges, setHasChanges] = useState(false);

  const [inputs, setInputs] = useState([]);

  const addField = () => {
    setInputs([...inputs, { value: "" }]);
  };

  const handleInputChange2 = (index, event) => {
    const newInputs = [...inputs];
    newInputs[index].value = event.target.value;
    setInputs(newInputs);
  };

  const scrapeContent = async (images_scraping_en, newLinks) => {
    const scrapeContentApiData = new FormData();

    const basePayload = [
      { key: "images_bucket", value: config.aws_bucket },
      { key: "images_folder", value: "business" },
      {
        key: "images_prefix",
        value: props?.data?.username,
      },
      {
        key: "images_max_png_size_b",
        value: 1048576,
      },
      {
        key: "images_compression",
        value: 0,
      },
      {
        key: "remove_duplicates_thr",
        value: 90,
      },
      { key: "user_id", value: props?.data?._id },
      { key: "images_min_size_b", value: 10240 },
      {
        key: "instagram_engine",
        value: "BrightData",
      },
      {
        key: "linkedin_engine",
        value: "BrightData",
      },
      {
        key: "filtering_posts_num",
        value: 300,
      },
      {
        key: "filtering_start_date",
        value: "",
      },
      {
        key: "filtering_end_date",
        value: "",
      },
      {
        key: "max_upload_size_mb",
        value: 80,
      },
      {
        key: "attributes_file",
        value: new Blob([JSON.stringify(targetAttributesFile)], {
          type: "application/json",
        }),
      },
      {
        key: "images_scraping_en",
        value: images_scraping_en,
      },
    ];

    if (newLinks.linkedin_link) {
      basePayload.push({
        key: "linkedin_url",
        value: newLinks.linkedin_link,
      });
    }
    if (newLinks.instagram_handle) {
      basePayload.push({
        key: "instagram_url",
        value: newLinks.instagram_handle,
      });
    }
    if (newLinks.website_link) {
      basePayload.push({
        key: "webpage_url",
        value: newLinks.website_link,
      });
    }

    basePayload.forEach(({ key, value }) =>
      scrapeContentApiData.append(key, value)
    );

    await http.post(
      `${config.api_url}/business/scrape_content`,
      scrapeContentApiData
    );

    toast(
      <ToastMsg message="Data sent for scrapping" />,
      config.success_toast_config
    );
  };

  const removeField = (index) => {
    const newInputs = [...inputs];
    newInputs.splice(index, 1);
    setInputs(newInputs);
  };

  const galleryTitles = [
    { key: "project_renders_media", title: "Project Photos & Renders" },
    { key: "completed_products_media", title: "Products | Materials" },
    { key: "sites_inprogress_media", title: "Sites In Progress" },
    { key: "others_media", title: "Others" },
  ];

  const TOTAL_STORAGE =
    props?.data?.subscription?.plan?.features?.imagesLimit || 200;

  const totalImages = galleryTitles.reduce((sum, { key }) => {
    return sum + (props?.data[key]?.length || 0);
  }, 0);

  const hasAdditionalWeblinks =
    props?.data?.subscription?.plan?.features?.externalLinksLimit > 0;

  const isInputDisabled = (field) => {
    return !!props?.data?.[field]; // Disable if field has a value
  };

  const [formError, setFormError] = useState({});
  const BusinessContext = useBusinessContext();

  useEffect(() => {
    if (props.data) {
      setFormData({
        website_link: props.data.website_link || "",
        instagram_handle: props.data.instagram_handle || "",
        linkedin_link: props.data.linkedin_link || "",
      });
      if (props.data.additional_web_urls) {
        setInputs(
          props.data.additional_web_urls.map((url) => ({ value: url }))
        );
      }
    }
  }, [props.data]);

  useEffect(() => {
    if (!props.data) return;

    const hasNewEntries =
      (formData.website_link && !props.data.website_link) ||
      (formData.instagram_handle && !props.data.instagram_handle) ||
      (formData.linkedin_link && !props.data.linkedin_link) ||
      inputs.length > (props.data.additional_web_urls?.length || 0);

    setHasChanges(hasNewEntries);
  }, [formData, inputs, props.data]);

  const handleInputChange = (e) => {
    setFormData((prevData) => ({
      ...prevData,
      [e.target.name]: e.target.value,
    }));
  };

  const validationSchema = (data) => {
    const schemaObj = {
      website_link: Joi.string()
        .label("Website")
        .allow("")
        .custom((value, helpers) => {
          const urlRegex =
            /^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w.-]+)+[\w\-._~:/?#[\]@!$&'()*+,;=.]+$/;
          if (value && !urlRegex.test(value)) {
            return helpers.message("Invalid website URL");
          }
          return value;
        }),
      additional_web_urls: Joi.array().items(
        Joi.string()
          .label("Website")
          .allow("")
          .custom((value, helpers) => {
            const urlRegex =
              /^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w.-]+)+[\w\-._~:/?#[\]@!$&'()*+,;=.]+$/;
            if (value && !urlRegex.test(value)) {
              return helpers.message("Invalid website URL");
            }
            return value;
          })
      ),
      instagram_handle: Joi.string()
        .label("Instagram")
        .allow("")
        .custom((value, helpers) => {
          if (!value) return value;

          // Extract username from URL if provided
          let username = value;
          const urlPattern =
            /^(https?:\/\/)?(www\.)?instagram\.com\/(.*?)\/?$/i;
          const match = value.match(urlPattern);
          if (match) {
            username = match[3]; // Extract username from URL
          }

          // Remove leading '@' if present
          username = username.replace(/^@/, "");

          // Validate username format
          const usernameRegex = /^[a-zA-Z0-9._]{1,30}$/;
          if (!usernameRegex.test(username)) {
            return helpers.message(
              "Instagram username can only contain letters, numbers, periods, and underscores (up to 30 characters)"
            );
          }

          // Check length explicitly for clarity
          if (username.length > 30) {
            return helpers.message(
              "Instagram username cannot exceed 30 characters"
            );
          }

          return value; // Return original value for further processing
        }),
      linkedin_link: Joi.string()
        .label("LinkedIn")
        .allow("")
        .custom((value, helpers) => {
          if (!value) return value;

          const validDomainRegex =
            /^(https:\/\/)?([a-z]{2}\.)?(www\.)?(linkedin\.com|linkedin\.in)\//i;
          if (!validDomainRegex.test(value)) {
            return helpers.message("Invalid LinkedIn URL");
          }

          if (
            /^http:\/\/([a-z]{2}\.)?(www\.)?(linkedin\.com|linkedin\.in)/i.test(
              value
            )
          ) {
            return helpers.message(
              "Please use 'https://' instead of 'http://' for LinkedIn URLs"
            );
          }

          const validPathRegex =
            /^(https:\/\/)?([a-z]{2}\.)?(www\.)?(linkedin\.com|linkedin\.in)\/[a-z]+\/[\w-]+\/?$/i;
          if (!validPathRegex.test(value)) {
            return helpers.message(
              "Please enter a valid LinkedIn URL with a proper path (e.g., linkedin.com/in/username, linkedin.in/company/companyname)"
            );
          }

          return value;
        }),
    };

    const schema = Joi.object(schemaObj)
      .options({ allowUnknown: true, abortEarly: false })
      .custom((value, helpers) => {
        const requiredFields = [
          "website_link",
          "instagram_handle",
          "linkedin_link",
        ];
        const hasValue = requiredFields.some((field) => !!value[field]);
        if (!hasValue) {
          return helpers.message("Add at least one of the following");
        }
        return value;
      });
    const { error } = schema.validate(data, config.joiOptions);

    if (error) {
      const errors = {};
      error.details.forEach((detail) => {
        errors[detail.path[0]] = detail.message;
      });
      return errors;
    }

    return {}; // Return an empty object on successful validation
  };

  const handleSubmit = async () => {
    let errors = validationSchema(formData);

    if (errors.undefined) {
      errors["atleast_one"] = errors.undefined;
      delete errors.undefined;
    }

    if (Object.keys(errors).length) {
      setFormError(errors);
      return;
    }

    const newLinksAdded = Object.keys(formData).some(
      (key) => formData[key] && !props.data[key]
    );

    if (newLinksAdded && totalImages >= TOTAL_STORAGE) {
      toast(
        <ToastMsg
          message={`Cannot add new links. Storage limit of ${TOTAL_STORAGE} images reached. Please upgrade to a Supporter plan to add links and get images scraped.`}
          danger={true}
        />,
        config.error_toast_config
      );
      return;
    }

    setFormError({});

    // Format the links as needed
    let formattedData = { ...formData };
    formattedData.additional_web_urls = inputs.map((input) => input.value);

    // Add https:// prefix to website if not present
    if (
      formattedData.website_link &&
      !/^https?:\/\//i.test(formattedData.website_link)
    ) {
      formattedData.website_link = `https://${formattedData.website_link}`;
    }

    // Format Instagram handle
    if (formattedData.instagram_handle) {
      // Extract username from URL if provided
      let username = formattedData.instagram_handle;
      const urlPattern = /^(https?:\/\/)?(www\.)?instagram\.com\/(.*?)\/?$/i;
      const match = formattedData.instagram_handle.match(urlPattern);
      if (match) {
        username = match[3]; // Extract username from URL
      }

      // Remove leading '@' if present
      username = username.replace(/^@/, "");

      // Store the cleaned URL
      formattedData.instagram_handle = `https://www.instagram.com/${username}`;
    }

    const newly_added_links = {};
    for (const key in formattedData) {
      if (
        Object.prototype.hasOwnProperty.call(formattedData, key) &&
        formattedData[key] &&
        !props.data[key]
      ) {
        newly_added_links[key] = formattedData[key];
      }
    }
    formattedData.newly_added_links = newly_added_links;

    const response = await http.post(
      `${config.api_url}/business/business-details/${props.data?._id}`,
      formattedData
    );

    if (response?.message) {
      response.message = response.message.trim();
    }

    if (
      response?.status === 200 &&
      response?.message === "Details updated Successfullly"
    ) {
      // await scrapeContent(true, newly_added_links);

      BusinessContext.update({
        ...BusinessContext.data,
        ...formattedData,
      });

      // toast(
      //   <ToastMsg message="Changes updated successfully" />,
      //   config.success_toast_config
      // );
      window.dispatchEvent(new Event("saveSuccess"));

      props.onHide();
    } else {
      toast(
        <ToastMsg
          message={response?.message || "Something went wrong"}
          danger
        />,
        config.error_toast_config
      );
    }
  };

  return (
    <>
      <Modal
        {...props}
        centered
        className={`Complete_actions_popup edit_social_link_b_connect_edit ${theme === "dark" && "edit_social_link_b_connect_edit--dark-mode"}`}
        backdropClassName="custom-backdrop"
      >
        <Modal.Header>
          <button
            className="Complete_actions_popup_cancel_btn"
            onClick={() => {
              if (props.data) {
                setFormData({
                  website_link: props.data.website_link || "",
                  instagram_handle: props.data.instagram_handle || "",
                  linkedin_link: props.data.linkedin_link || "",
                });
                if (props.data.additional_web_urls) {
                  setInputs(
                    props.data.additional_web_urls.map((url) => ({
                      value: url,
                    }))
                  );
                }
              }
              props.onHide();
            }}
          >
            <img
              src={modalClose}
              alt="close-icon"
              className="Complete_actions_popup_cancel_btn_img"
            />
          </button>
        </Modal.Header>
        <Modal.Body>
          <h2 className="Complete_actions_popup_m_heading social_link_edi_heading">
            Edit Social Links
          </h2>

          {formError.atleast_one && (
            <div className="error-message global-error">
              {formError.atleast_one}
            </div>
          )}

          <div className="social_links_edit_wrapper">
            <div className="social_links_edit_single_container">
              <div className="social_edit_link_icon">
                <img
                  src={linkedInIcon}
                  alt="web"
                  className="social_edit_link_icon_img"
                />
              </div>
              <div className="social_edit_link_input">
                {/* <input
                  type="text"
                  className="input_edit_links"
                  placeholder="LinkedIn"
                  name="linkedin_link"
                  value={formData.linkedin_link}
                  onChange={handleInputChange}
                  disabled={isInputDisabled("linkedin_link")}
                /> */}
                <textarea
                  type="text"
                  className="input_edit_links"
                  placeholder="LinkedIn"
                  name="linkedin_link"
                  value={formData.linkedin_link}
                  onChange={handleInputChange}
                  disabled={isInputDisabled("linkedin_link")}
                ></textarea>
                {isInputDisabled("linkedin_link") && (
                  <img src={lockIcon} alt="Lock Icon" className="lock_icon" />
                )}

                {formError.linkedin_link && (
                  <div className="error_text">{formError.linkedin_link}</div>
                )}
              </div>
            </div>
            <div className="social_links_edit_single_container">
              <div className="social_edit_link_icon">
                <img
                  src={instaIcon}
                  alt="web"
                  className="social_edit_link_icon_img"
                />
              </div>
              <div className="social_edit_link_input">
                {/* <input
                  type="text"
                  className="input_edit_links"
                  placeholder="Instagram"
                  name="instagram_handle"
                  value={formData.instagram_handle}
                  onChange={handleInputChange}
                  disabled={isInputDisabled("instagram_handle")}
                /> */}

                <textarea
                  type="text"
                  className="input_edit_links"
                  placeholder="Instagram"
                  name="instagram_handle"
                  value={formData.instagram_handle}
                  onChange={handleInputChange}
                  disabled={isInputDisabled("instagram_handle")}
                ></textarea>
                {isInputDisabled("instagram_handle") && (
                  <img src={lockIcon} alt="Lock Icon" className="lock_icon" />
                )}

                {formError.instagram_handle && (
                  <div className="error_text">{formError.instagram_handle}</div>
                )}
              </div>
            </div>

            <div className="social_links_edit_single_container">
              <div className="social_edit_link_icon">
                <img
                  src={webIcon}
                  alt="web"
                  className="social_edit_link_icon_img"
                />
              </div>
              <div className="social_edit_link_input">
                <input
                  type="text"
                  className="input_edit_links"
                  placeholder="Website"
                  name="website_link"
                  value={formData.website_link}
                  onChange={handleInputChange}
                  disabled={isInputDisabled("website_link")}
                />

                {isInputDisabled("website_link") && (
                  <img src={lockIcon} alt="Lock Icon" className="lock_icon" />
                )}

                {formError.website_link && (
                  <div className="error_text">{formError.website_link}</div>
                )}
              </div>
            </div>
            {hasAdditionalWeblinks && inputs?.length > 0 && (
              <h2 className="Complete_actions_popup_m_heading social_link_edi_heading">
                Additional Web Links
              </h2>
            )}
            {inputs.map((input, index) => (
              <div className="social_links_edit_single_container">
                <div className="social_edit_link_icon">
                  <img
                    src={webIcon}
                    alt="web"
                    className="social_edit_link_icon_img"
                  />
                </div>
                <div className="social_edit_link_input">
                  <input
                    type="text"
                    className="input_edit_links"
                    placeholder="Website"
                    name="website_link"
                    value={input.value}
                    onChange={(e) => handleInputChange2(index, e)}
                    // disabled={isInputDisabled("website_link")}
                  />

                  {/* <img src={lockIcon} alt="Lock Icon" className="lock_icon" /> */}

                  {formError.website_link && (
                    <div className="error_text">{formError.website_link}</div>
                  )}
                </div>
                <img
                  src={modalClose}
                  alt="Cross Icon"
                  className="modal_close_icon"
                  onClick={() => removeField(index)}
                />
              </div>
            ))}

            {hasAdditionalWeblinks &&
              inputs.length <
                props?.data?.subscription?.plan?.features
                  ?.externalLinksLimit && (
                <div className="add_new_field" onClick={addField}>
                  <img src={plusicon} alt="" />
                  <p>Add Webiste Field</p>
                </div>
              )}
          </div>
          <button
            type="submit"
            className="confirmation_common_btn confirmation_cmn_edit_coonect_btn"
            onClick={handleSubmit}
            disabled={!hasChanges}
          >
            Save Changes
          </button>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default EditSocialLinkPopup;
